import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import BlockIcon from '@material-ui/icons/Block';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Spinner } from '../../components/UI';
import TableSearchBar from '../../components/TableSearchBar';
import useUserManagement from './useUserManagement';
import { USER_MANAGEMENT_TABLE_COLUMNS } from './user.helper';
import {
    ScopeCell,
    UserTableCell,
    UserTableHeadCell,
} from './utility.component.user';
import UserScopeModal from '../../components/Modals/UserScopeModal';
import AdminUserModal from '../../components/Modals/AdminUserModal';

const formatDate = (value?: string) =>
    value ? new Date(value).toLocaleDateString('en-IN') : '-';

const UserManagementPage: React.FC = () => {
    const {
        users,
        totalCount,
        pageNo,
        pageSize,
        loading,
        styles,
        permissions,
        searchTextValue,
        setSearchTextValue,
        handleChangePage,
        handleChangeRowsPerPage,
        handleSearchSubmit,
        handleSearchClear,
        appliedSearch,
        scopeModalUser,
        setScopeModalUser,
        handleScopeSave,
        confirmDeactivate,
        setConfirmDeactivate,
        handleConfirmDeactivate,
        createOpen,
        setCreateOpen,
        stores,
        zones,
        assignableRoles,
        vendorOrgs,
        canEditScope,
        canDeactivate,
        refetch,
        roleLabel,
    } = useUserManagement();

    const renderCell = (row: any, key: string) => {
        switch (key) {
            case 'role':
                return roleLabel(row.role);
            case 'scopeValues':
                return (
                    <>
                        <ScopeCell
                            values={row.scopeValues}
                            scopeType={row.scopeType}
                        />
                        {row.scopeMissing && (
                            <div className={styles.missingScope}>
                                No access assigned
                            </div>
                        )}
                    </>
                );
            case 'createdOn':
                return formatDate(row.createdOn);
            case 'actions':
                return (
                    <Box display="flex" style={{ gap: 4 }}>
                        {canEditScope(row) && (
                            <Tooltip title="Edit access">
                                <IconButton
                                    size="small"
                                    onClick={() => setScopeModalUser(row)}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canDeactivate(row) && (
                            <Tooltip title="Deactivate user">
                                <IconButton
                                    size="small"
                                    onClick={() => setConfirmDeactivate(row)}
                                >
                                    <BlockIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            default:
                return row[key] ?? '-';
        }
    };

    return (
        <Paper className={styles.container}>
            <Box className={styles.toolbar}>
                <Typography variant="h6">User Management</Typography>
                <Box className={styles.toolbarActions}>
                    <TableSearchBar
                        placeholder="Search by name, mobile, store, zone or org"
                        value={searchTextValue}
                        onChange={setSearchTextValue}
                        onSearch={handleSearchSubmit}
                        onClear={handleSearchClear}
                        isDirty={searchTextValue.trim() !== appliedSearch}
                        hasApplied={!!appliedSearch}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={refetch}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {permissions.create && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setCreateOpen(true)}
                        >
                            Create User
                        </Button>
                    )}
                </Box>
            </Box>
            <Divider />

            <Spinner loading={loading}>
                <>
                    <TableContainer className={styles.tableWrapper}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {USER_MANAGEMENT_TABLE_COLUMNS.map(
                                        (column) => (
                                            <UserTableHeadCell
                                                key={column.key}
                                                style={{
                                                    width: `${column.width}%`,
                                                }}
                                            >
                                                {column.label}
                                            </UserTableHeadCell>
                                        ),
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((row: any) => (
                                    <TableRow key={row.id} hover>
                                        {USER_MANAGEMENT_TABLE_COLUMNS.map(
                                            (column) => (
                                                <UserTableCell key={column.key}>
                                                    {renderCell(
                                                        row,
                                                        column.key,
                                                    )}
                                                </UserTableCell>
                                            ),
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {users.length === 0 && (
                            <div className={styles.emptyState}>
                                No internal users found.
                            </div>
                        )}
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={pageNo}
                        rowsPerPage={pageSize}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        rowsPerPageOptions={
                            totalCount > 20 ? [20, 50, 100] : []
                        }
                    />
                </>
            </Spinner>

            {scopeModalUser && (
                <UserScopeModal
                    open
                    user={scopeModalUser}
                    stores={stores}
                    zones={zones}
                    onClose={() => setScopeModalUser(null)}
                    onSave={handleScopeSave}
                />
            )}

            {createOpen && (
                <AdminUserModal
                    open
                    mode="create"
                    assignableRoles={assignableRoles}
                    stores={stores}
                    zones={zones}
                    vendorOrgs={vendorOrgs}
                    onClose={() => setCreateOpen(false)}
                    onSuccess={() => {
                        setCreateOpen(false);
                        refetch();
                    }}
                />
            )}

            <Dialog
                open={!!confirmDeactivate}
                onClose={() => setConfirmDeactivate(null)}
            >
                {/* Matches the rider deactivation dialog so the two read as one product. */}
                <DialogTitle
                    disableTypography
                    style={{ padding: '16px 24px 8px' }}
                >
                    <Typography
                        style={{
                            fontFamily: 'Nunito',
                            fontWeight: 700,
                            fontSize: 18,
                            color: '#14112d',
                        }}
                    >
                        Deactivate User
                    </Typography>
                </DialogTitle>
                <DialogContent style={{ padding: '8px 24px 16px' }}>
                    <DialogContentText
                        style={{
                            fontFamily: 'Nunito',
                            fontWeight: 600,
                            fontSize: 16,
                            color: '#515f6d',
                            marginBottom: 0,
                        }}
                    >
                        Do you want to deactivate{' '}
                        <span style={{ fontWeight: 700, color: '#14112d' }}>
                            {confirmDeactivate?.name}
                        </span>
                        ? They will no longer be able to log in. Their record
                        and history are retained.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '8px 24px 16px', gap: 8 }}>
                    <Button
                        onClick={() => setConfirmDeactivate(null)}
                        style={{
                            fontFamily: 'Nunito',
                            fontWeight: 700,
                            color: '#43419e',
                            borderRadius: 8,
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDeactivate}
                        variant="contained"
                        style={{
                            fontFamily: 'Nunito',
                            fontWeight: 700,
                            fontSize: 14,
                            backgroundColor: '#43419e',
                            color: '#fff',
                            borderRadius: 8,
                            textTransform: 'none',
                            padding: '6px 20px',
                        }}
                    >
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default UserManagementPage;
